import { create } from "zustand"
import type {
  PersonalExpense,
  CreatePersonalExpensePayload,
  UpdatePersonalExpensePayload,
} from "@/types/personal-expenses"
import {
  fetchPersonalExpenses,
  createPersonalExpense,
  fetchPersonalExpenseDetail,
  updatePersonalExpense,
  deletePersonalExpense,
} from "@/services/personal-expenses"

interface PersonalExpensesState {
  expenses: PersonalExpense[]
  currentExpense: PersonalExpense | null
  isLoading: boolean
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
  error: string | null
  fetch: () => Promise<void>
  create: (payload: CreatePersonalExpensePayload) => Promise<boolean>
  fetchDetail: (id: number) => Promise<void>
  update: (id: number, payload: UpdatePersonalExpensePayload) => Promise<boolean>
  delete: (id: number) => Promise<boolean>
  clearCurrentExpense: () => void
}

export const usePersonalExpensesStore = create<PersonalExpensesState>((set) => ({
  expenses: [],
  currentExpense: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,

  fetch: async () => {
    set({ isLoading: true, error: null })

    try {
      const response = await fetchPersonalExpenses()

      if (response.status !== 1) {
        throw new Error(response.message || "Failed to fetch personal expenses")
      }

      set({ expenses: response.data, isLoading: false })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch personal expenses"
      set({ error: message, isLoading: false })
    }
  },

  create: async (payload: CreatePersonalExpensePayload) => {
    set({ isCreating: true, error: null })

    try {
      const response = await createPersonalExpense(payload)

      if (response.status !== 1) {
        throw new Error(response.message || "Failed to create personal expense")
      }

      set((state) => ({
        expenses: [response.data, ...state.expenses],
        isCreating: false,
      }))

      return true
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create personal expense"
      set({ error: message, isCreating: false })
      return false
    }
  },

  fetchDetail: async (id: number) => {
    set({ isLoading: true, error: null, currentExpense: null })

    try {
      const response = await fetchPersonalExpenseDetail(id)

      if (response.status !== 1) {
        throw new Error(response.message || "Failed to fetch personal expense details")
      }

      set({ currentExpense: response.data, isLoading: false })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to fetch personal expense details"
      set({ error: message, isLoading: false })
    }
  },

  update: async (id: number, payload: UpdatePersonalExpensePayload) => {
    set({ isUpdating: true, error: null })

    try {
      const response = await updatePersonalExpense(id, payload)

      if (response.status !== 1) {
        throw new Error(response.message || "Failed to update personal expense")
      }

      set((state) => ({
        currentExpense: response.data,
        expenses: state.expenses.map((e) =>
          e.id === id ? response.data : e
        ),
        isUpdating: false,
      }))

      return true
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to update personal expense"
      set({ error: message, isUpdating: false })
      return false
    }
  },

  delete: async (id: number) => {
    set({ isDeleting: true, error: null })

    try {
      const response = await deletePersonalExpense(id)

      if (response.status !== 1) {
        throw new Error(response.message || "Failed to delete personal expense")
      }

      set((state) => ({
        expenses: state.expenses.filter((e) => e.id !== id),
        currentExpense: null,
        isDeleting: false,
      }))

      return true
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to delete personal expense"
      set({ error: message, isDeleting: false })
      return false
    }
  },

  clearCurrentExpense: () => {
    set({ currentExpense: null, error: null })
  },
}))
